import type { CSSProperties, ReactNode } from "react";

import { XIcon } from "@phosphor-icons/react";
import * as Dialog from "@radix-ui/react-dialog";
import {
  Box,
  Flex,
  Heading,
  IconButton,
  ScrollArea,
  Text,
  Theme,
} from "@radix-ui/themes";

import { ORDER_TICKET_DRAWER_WIDTH } from "./OrderTicketDrawer.types";
import "./orderTicketDrawer.css";

export interface OrderTicketDrawerPresentationProps {
  open: boolean;
  /**
   * Modal below the push breakpoint only. It is passed straight to Radix's
   * `Dialog.Root`, so push mode (`modal={false}`) is genuinely non-modal: no
   * scrim, no focus trap, and the workspace beside the drawer stays
   * interactive. Radix swaps content implementations when it changes, which
   * remounts this subtree, so nothing stateful may live below here.
   */
  modal: boolean;
  /**
   * The engine tab's dedicated drawer slot, handed to `Dialog.Portal` in both
   * modal and push modes so the portal target never changes across the
   * responsive breakpoint: swapping it (or dropping to `undefined`) would move
   * the drawer to a different part of the tree on every crossing.
   */
  container: HTMLElement | null;
  reducedMotion: boolean;
  onOpenChange: (open: boolean) => void;
  ticketBody: ReactNode;
}

const CONTENT_CLASS = "workspace-order-ticket-drawer";
const OVERLAY_CLASS = "workspace-order-ticket-overlay";

export const OrderTicketDrawerPresentation = ({
  open,
  modal,
  container,
  reducedMotion,
  onOpenChange,
  ticketBody,
}: OrderTicketDrawerPresentationProps) => {
  // Modal mode covers the viewport regardless of the slot it is portalled
  // into; push mode fills the slot, which is a viewport-height sticky column.
  const contentStyle: CSSProperties = {
    position: modal ? "fixed" : "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    width: modal ? "100vw" : ORDER_TICKET_DRAWER_WIDTH,
    maxWidth: "100vw",
    backgroundColor: "var(--color-panel-solid)",
    borderLeft: "1px solid var(--gray-a5)",
    outline: "none",
    zIndex: 50,
    ...(reducedMotion ? { animation: "none", transition: "none" } : {}),
  };

  // In push mode the drawer is not a dialog: keep Radix from hiding, trapping,
  // or pointer-blocking the workspace, and keep an outside click from either
  // dismissing the drawer or stealing focus into it. `preventDefault` here
  // stops Radix's own dismiss/focus handling only — the DOM event still
  // reaches the workspace, so it stays fully interactive.
  const nonModalContentProps = modal
    ? {}
    : {
        onOpenAutoFocus: (event: Event) => event.preventDefault(),
        onCloseAutoFocus: (event: Event) => event.preventDefault(),
        onInteractOutside: (event: Event) => event.preventDefault(),
      };

  const reducedMotionAttr = reducedMotion
    ? { "data-reduced-motion": "true" as const }
    : {};

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange} modal={modal}>
      <Dialog.Portal container={container}>
        <Theme>
          {modal && (
            <Dialog.Overlay
              data-testid="order-ticket-overlay"
              className={`${OVERLAY_CLASS} fixed inset-0 z-[49] bg-[var(--black-a6)]`}
              {...reducedMotionAttr}
              style={reducedMotion ? { animation: "none" } : undefined}
            />
          )}
          <Dialog.Content asChild {...nonModalContentProps}>
            {/* Radix puts `role="dialog"` and `aria-labelledby` pointing at
                `Dialog.Title` on this element, so it needs no own label. */}
            <section
              data-testid="order-ticket-content"
              className={CONTENT_CLASS}
              {...reducedMotionAttr}
              style={contentStyle}
            >
              <Flex direction="column" height="100%" className="min-h-0">
                <Flex
                  align="center"
                  justify="between"
                  gap="2"
                  px="3"
                  py="2"
                  className="border-[color:var(--gray-a5)] border-b"
                >
                  <Box>
                    <Heading asChild size="4">
                      <Dialog.Title>Order Ticket</Dialog.Title>
                    </Heading>
                    <Text asChild size="1" color="gray">
                      <Dialog.Description>
                        Search for an asset or review your order.
                      </Dialog.Description>
                    </Text>
                  </Box>
                  <Dialog.Close asChild>
                    <IconButton
                      type="button"
                      variant="ghost"
                      color="gray"
                      aria-label="Close order ticket"
                    >
                      <XIcon aria-hidden size={18} />
                    </IconButton>
                  </Dialog.Close>
                </Flex>
                <ScrollArea
                  type="auto"
                  scrollbars="vertical"
                  className="min-h-0 flex-1"
                >
                  {ticketBody}
                </ScrollArea>
              </Flex>
            </section>
          </Dialog.Content>
        </Theme>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
